import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Award } from 'lucide-react';

const MensMemberships = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contact');
      if (element) {
        const offset = 90;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const membershipTiers = [
    {
      name: 'BRONZE',
      subtitle: 'Men\'s Essential',
      price: '$99',
      duration: '60 minutes',
      benefits: [
        'Monthly Hydrafacial (Signature)',
        'B12 or NAD+ Vitamin Shot',
        'Relaxing Scalp Massage'
      ],
      color: 'from-[#8B6F47] to-[#6B5537]'
    },
    {
      name: 'SILVER',
      subtitle: 'Gentleman\'s Rejuvenation',
      price: '$149',
      duration: '90 minutes',
      benefits: [
        'Monthly Hydrafacial (Deluxe)',
        'B12 or NAD+ Vitamin Shot',
        'Stress Relieving Neck and Shoulder Massage'
      ],
      color: 'from-[#7D8895] to-[#5C6B7A]',
      featured: true
    },
    {
      name: 'GOLD',
      subtitle: 'Ultimate Spa Grooming',
      price: '$199',
      duration: '2 hours',
      benefits: [
        'Monthly Hydrafacial (Deluxe)',
        'B12 or NAD+ Vitamin Shot',
        'Laser Hair Removal or Waxing for Small Area',
        'Swedish or Deep Tissue Massage'
      ],
      color: 'from-gold/90 to-[#B8956A]'
    }
  ];

  const highlights = [
    {
      title: 'Tailored for Men',
      description: 'Skincare Solutions for Your Needs'
    },
    {
      title: 'Deep Cleansing',
      description: 'Remove Impurities and Refresh Your Skin'
    },
    {
      title: 'Gentle Exfoliation',
      description: 'Promote Smooth and Clear Skin'
    },
    {
      title: 'Skin Rejuvenation',
      description: 'Boost Your Confidence'
    },
    {
      title: 'Results that Speak for Themselves',
      description: ''
    }
  ];

  return (
    <>
      <Helmet>
        <title>Men's Exclusive Memberships - Bronze, Silver, Gold | Century Aesthetics</title>
        <meta name="description" content="Men's grooming and wellness memberships starting at $99/month. Get monthly HydraFacials, B12 shots, massages, and more. Tailored skincare solutions for men." />
        <meta name="keywords" content="men's spa membership, men's grooming, hydrafacial for men, men's skincare, wellness membership men, vitamin shots, men's massage" />
        <link rel="canonical" href="https://www.centuryaesthetics.org/mens-memberships" />
        
        <meta property="og:title" content="Men's Exclusive Memberships | Century Longevity & Aesthetics" />
        <meta property="og:description" content="Elevate your grooming routine with men's exclusive spa memberships. Professional treatments tailored for men's skincare needs." />
        <meta property="og:url" content="https://www.centuryaesthetics.org/mens-memberships" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-nude/20 pt-28">
      
      {/* Hero Section */}
      <section className="py-16 relative overflow-hidden" aria-label="Men's Memberships Hero">
        <div className="absolute inset-0 bg-gradient-to-br from-olive-darkest/5 to-gold/5"></div>
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-6xl md:text-7xl font-heading font-bold text-olive-darkest mb-2 tracking-tight">
              skinvestment
            </h1>
            <p className="text-3xl md:text-4xl font-heading font-bold text-olive-darkest mb-6 tracking-wider">
              MEN EXCLUSIVE MEMBERSHIPS
            </p>
            <p className="text-xl text-olive-darkest/80 font-light tracking-wide uppercase mb-4">
              New Year Promo
            </p>
            <div className="w-32 h-1 bg-gold mx-auto"></div>
          </motion.div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-12 bg-white" aria-label="Membership Benefits Highlights">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <Award className="w-8 h-8 text-gold mx-auto mb-3" aria-hidden="true" />
                <h3 className="text-sm font-bold text-olive-darkest mb-1">
                  {highlight.title}
                </h3>
                {highlight.description && (
                  <p className="text-xs text-olive-darkest/70">
                    {highlight.description}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Cards */}
      <section className="py-12 pb-20" aria-label="Men's Membership Plans">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {membershipTiers.map((tier, index) => (
              <motion.article
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className={`relative ${tier.featured ? 'md:-mt-4 md:mb-4' : ''}`}
              >
                {tier.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gold text-olive-darkest px-6 py-2 rounded-full text-sm font-semibold tracking-wider shadow-lg">
                    BEST VALUE
                  </div>
                )}
                <div className={`bg-gradient-to-br ${tier.color} rounded-2xl p-8 shadow-2xl h-full flex flex-col border-2 ${tier.featured ? 'border-gold' : 'border-cream/30'}`}>
                  {/* Header */}
                  <header className="text-center mb-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                      <Award className="w-8 h-8 text-cream" aria-hidden="true" />
                    </div>
                    <h2 className="text-3xl font-heading font-bold text-cream tracking-wider mb-2">
                      {tier.name}
                    </h2>
                    <p className="text-lg text-cream/90 mb-4">
                      {tier.subtitle}
                    </p>
                    <div className="flex items-baseline justify-center gap-2 mb-2">
                      <span className="text-5xl font-heading font-light text-white">
                        {tier.price}
                      </span>
                    </div>
                    <p className="text-cream/80 text-sm">
                      {tier.duration}
                    </p>
                    <div className="w-16 h-0.5 bg-cream/30 mx-auto mt-4"></div>
                  </header>

                  {/* Benefits */}
                  <ul className="space-y-4 flex-1">
                    {tier.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-cream flex-shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
                        <span className="text-cream/95 text-sm leading-relaxed font-medium">
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <motion.button
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={scrollToContact}
                    className="mt-8 w-full bg-cream text-olive-darkest px-6 py-4 rounded-lg font-semibold text-lg tracking-wide hover:bg-white transition-all duration-300 shadow-lg"
                    aria-label={`Join ${tier.name} membership`}
                  >
                    Join Now
                  </motion.button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-8 bg-nude/30">
        <div className="max-w-[1200px] mx-auto px-6">
          <p className="text-center text-sm text-olive-darkest/70 leading-relaxed">
            These packages offer a range of options to cater to different skincare needs and preferences,
            from basic rejuvenation to more luxurious and comprehensive treatments.
            <br />
            Prices and durations are approximate and may vary by location and salon.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-olive-darkest" aria-label="Elevate Your Grooming">
        <div className="max-w-[1400px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Elevate Your Grooming Routine
            </h2>
            <p className="text-lg text-cream/90 max-w-3xl mx-auto leading-relaxed mb-8">
              Our men's exclusive memberships are designed specifically for your skincare and wellness needs. Experience professional treatments that fit your lifestyle and help you look and feel your best.
            </p>
            <motion.button
              whileHover={{ y: -2, scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={scrollToContact}
              className="bg-gold text-olive-darkest px-10 py-4 rounded-lg font-semibold text-lg tracking-wide hover:bg-cream transition-all duration-300 shadow-xl"
              aria-label="Schedule consultation for men's membership"
            >
              Schedule Consultation
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-16 bg-cream"></div>
    </div>
    </>
  );
};

export default MensMemberships;